import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, type AnalysisPayload, type ExtractionRow, type SystemRow } from '../api/client';
import { uploadFileToGemini } from '../lib/geminiUpload';
import { extractPdfSubset } from '../lib/pdf';
import { autoCropLegend } from '../lib/legendCrops';
import { ProjectSteps } from '../components/ProjectSteps';
import { useLang, type Bilingual } from '../i18n/lang';
import { G } from '../i18n/glossary';

// Each uploaded file is read by its own type + page selection.
interface UploadItem { file: File; type: string; spec: string }
const DOC_TYPES: { v: string; label: string }[] = [
  { v: 'plans', label: 'Plans / Drawings' },
  { v: 'spec', label: 'Specification' },
  { v: 'addenda', label: 'Addendum' },
  { v: 'bid_form', label: 'Bid form / ITB' },
];
const DOC_LABEL: Record<string, string> = Object.fromEntries(DOC_TYPES.map((d) => [d.v, d.label]));

function guessType(name: string): string {
  const n = name.toLowerCase();
  if (/addend/.test(n)) return 'addenda';
  if (/spec/.test(n)) return 'spec';
  if (/\bbid\b|itb|rfp/.test(n)) return 'bid_form';
  return 'plans';
}

export function ProjectWorkspace() {
  const { t } = useLang();
  const { id = '' } = useParams();
  const [data, setData] = useState<AnalysisPayload | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [source, setSource] = useState<'gemini' | 'claude' | 'sample' | 'unavailable' | null>(null);
  const [aiWarning, setAiWarning] = useState('');
  const [items, setItems] = useState<UploadItem[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  async function load() {
    try {
      setData(await api.getAnalysis(id));
    } catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Failed to load', fil: 'Hindi na-load' })); }
  }
  useEffect(() => { void load(); }, [id]);

  async function analyze() {
    if (!items.length) { setError(t({ en: 'Choose one or more PDFs first (spec and/or plans).', fil: 'Pumili muna ng isa o higit pang PDF (spec at/o plans).' })); return; }
    setBusy(true); setError(''); setSource(null); setAiWarning('');
    try {
      // Upload each PDF to Gemini's File API and pass the URI to ingest — Gemini
      // then reads ALL pages of EVERY file natively (no inline size cap, no page
      // sampling, no risk of missing the legend). Verified against a real 51-page,
      // 29 MB scanned IFC planset: legend on page 12, 56 symbols extracted (2026-06-06).
      const files: { mimeType: string; fileUri: string }[] = [];
      const ctx: string[] = [];
      let plansFile: File | null = null;        // remember the plan PDF so we can crop symbols from it after analysis
      let plansSpec = '';
      for (const it of items) {
        if (it.type === 'plans' && !plansFile) { plansFile = it.file; plansSpec = it.spec; }
        // 1) Store the ORIGINAL file in R2 (project record keeps the full PDF).
        setStatus(`${t({ en: 'Uploading', fil: 'Ina-upload ang' })} ${it.file.name} ${t({ en: 'to project storage…', fil: 'sa project storage…' })}`);
        try { await api.uploadDocument(id, it.file, it.type); }
        catch (e) { console.warn('Raw file storage skipped:', e); }
        // 2) Extract only the requested pages BEFORE sending to AI. A 500-page
        //    planset with "47-67" entered yields a 21-page subset Gemini reads in
        //    seconds. Blank or AUTO sends the original.
        setStatus(`${t({ en: 'Extracting requested pages from', fil: 'Kinukuha ang mga hiniling na pahina mula sa' })} ${it.file.name}…`);
        let toAi: File = it.file;
        let pagesKept = 0, totalPages = 0;
        try {
          const sub = await extractPdfSubset(it.file, it.spec);
          toAi = sub.file; pagesKept = sub.pagesKept; totalPages = sub.totalPages;
        } catch (e) {
          console.warn('Page subset failed; will send original file:', e);
        }
        const ofMsg = pagesKept && totalPages && pagesKept < totalPages ? ` (${pagesKept} ${t({ en: 'of', fil: 'sa' })} ${totalPages} ${t({ en: 'pages', fil: 'na pahina' })})` : ` (${t({ en: 'full PDF', fil: 'buong PDF' })})`;
        // 3) Upload the (possibly subset) PDF to Gemini's File API.
        setStatus(`${t({ en: 'Uploading', fil: 'Ina-upload ang' })} ${it.file.name} ${t({ en: 'to AI', fil: 'sa AI' })}${ofMsg}…`);
        try {
          const up = await uploadFileToGemini(toAi);
          files.push({ mimeType: up.mimeType, fileUri: up.fileUri });
          ctx.push(`${DOC_LABEL[it.type] ?? it.type}: ${it.file.name}${ofMsg}`);
        } catch (e) {
          console.warn('AI upload failed, will not attempt fallback:', e);
          setError(e instanceof Error ? e.message : t({ en: 'AI upload failed', fil: 'Nabigo ang AI upload' }));
        }
      }
      if (!files.length) { setError(t({ en: 'Could not upload any documents to the AI.', fil: 'Walang dokumentong na-upload sa AI.' })); return; }
      setStatus(`${t({ en: 'Analyzing', fil: 'Sinusuri ang' })} ${files.length} ${t({ en: 'document(s) — reading every page (legend, symbols, notes, systems)…', fil: 'na dokumento — binabasa ang bawat pahina (legend, symbols, notes, systems)…' })}`);
      const res = await api.ingest(id, { filename: ctx.join(' · '), pages: [], files });
      setSource(res.source);
      if (res.source === 'unavailable' && res.warning) setAiWarning(res.warning);
      await load();
      setStatus(t({ en: 'Analysis complete.', fil: 'Tapos na ang pagsusuri.' }));

      // AUTO-CROP symbol images from the plan legend — no separate step needed.
      // Uses the plan PDF already in memory; finds the legend page; crops each
      // symbol's glyph and attaches it to the symbol record.
      if (plansFile) {
        try {
          const fresh = await api.getAnalysis(id);
          const syms = (fresh.symbols ?? []).map((s) => ({ id: s.id, symbol_tag: s.symbol_tag, legend_description: s.legend_description }));
          if (syms.length) {
            setStatus(`${t({ en: 'Cropping symbol images from the legend', fil: 'Kinukuha ang mga symbol image mula sa legend' })} (${syms.length} symbols)…`);
            const crop = await autoCropLegend(plansFile, id, syms, plansSpec, (p) => setStatus(p.message ?? t({ en: 'Cropping symbols…', fil: 'Kinukuha ang mga symbol…' })));
            await load();
            if (crop.matched > 0) {
              setStatus(`${t({ en: 'Analysis complete —', fil: 'Tapos na ang pagsusuri —' })} ${crop.matched} ${t({ en: 'of', fil: 'sa' })} ${syms.length} ${t({ en: 'symbol images cropped from page', fil: 'symbol image ang nakuha mula sa pahina' })} ${crop.legendPage}.`);
            } else {
              setStatus(t({ en: 'Analysis complete. Symbol images could not be cropped automatically — open “Get Symbol Images from Legend” to try a specific page.', fil: 'Tapos na ang pagsusuri. Hindi awtomatikong nakuha ang mga symbol image — buksan ang “Get Symbol Images from Legend” para subukan ang isang partikular na pahina.' }));
            }
          }
        } catch (cropErr) {
          console.warn('Auto symbol-crop failed:', cropErr);
          setStatus(t({ en: 'Analysis complete. (Automatic symbol-image cropping failed — you can run it manually from the Count Sheet.)', fil: 'Tapos na ang pagsusuri. (Nabigo ang awtomatikong pagkuha ng symbol image — maaari mong patakbuhin ito nang manu-mano mula sa Count Sheet.)' }));
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t({ en: 'Ingest failed', fil: 'Nabigo ang ingest' }));
      setStatus('');
    } finally {
      setBusy(false);
    }
  }

  async function toggle(sys: SystemRow) {
    const selected = !sys.selected;
    setData((d) => d && { ...d, systems: d.systems.map((s) => s.id === sys.id ? { ...s, selected: selected ? 1 : 0 } : s) });
    try { await api.toggleSystem(id, sys.id, selected); }
    catch { void load(); }
  }

  const byPrefix = (p: string) => (data?.extraction ?? []).filter((r) => r.field.startsWith(p));
  const flags = byPrefix('flag.');
  const specs = byPrefix('spec_section');
  const notes = byPrefix('note.');
  const reqs = byPrefix('requirement.');
  const scope = (k: string) => byPrefix(k);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div>
          <Link to="/" className="muted" style={{ fontSize: 13 }}>← {t({ en: 'Dashboard', fil: 'Dashboard' })}</Link>
          <h1 style={{ margin: '4px 0 0' }}>{data?.project?.name ?? t({ en: 'Project', fil: 'Proyekto' })}</h1>
          <p className="muted" style={{ margin: 0 }}>
            {[data?.project?.county, data?.project?.state].filter(Boolean).join(', ')}
            {data?.project?.status && <> · <span className={`pill pill-${data.project.status}`}>{data.project.status}</span></>}
          </p>
        </div>
      </div>

      <div style={{ marginTop: 14 }}><ProjectSteps current="scope" /></div>

      <div className="banner" style={{ marginTop: 14 }}>
        <strong>{t({ en: 'AI reads. Humans count.', fil: 'Nagbabasa ang AI. Nagbibilang ang tao.' })}</strong> {t({ en: 'Uploading a plan set extracts and analyzes the documents — it identifies systems, legend symbols, specs and conflicts. It never counts symbols.', fil: 'Ang pag-upload ng plan set ay kinukuha at sinusuri ang mga dokumento — tinutukoy nito ang mga system, legend symbols, specs at conflicts. Hindi ito kailanman nagbibilang ng symbols.' })}
      </div>

      {/* Upload */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h2>1 · {t({ en: 'Choose documents, set type & pages, then Analyze', fil: 'Pumili ng mga dokumento, itakda ang type at pages, tapos Analyze' })}</h2>
        <div className="field" style={{ marginBottom: 10 }}>
          <label>{t({ en: 'Documents (spec, plans, addenda)', fil: 'Mga Dokumento (spec, plans, addenda)' })}</label>
          <input ref={fileRef} type="file" accept="application/pdf" multiple disabled={busy}
            onChange={(e) => { setItems(Array.from(e.target.files ?? []).map((f) => ({ file: f, type: guessType(f.name), spec: '' }))); setStatus(''); setError(''); }} />
        </div>
        {items.length > 0 && (
          <table style={{ marginBottom: 8 }}>
            <thead><tr><th>{t({ en: 'File', fil: 'File' })}</th><th>{t({ en: 'What is it?', fil: 'Ano ito?' })}</th><th>{t({ en: 'Pages to read', fil: 'Mga pahinang babasahin' })}</th></tr></thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i}>
                  <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.file.name}</td>
                  <td>
                    <select value={it.type} disabled={busy} onChange={(e) => setItems((s) => s.map((x, j) => j === i ? { ...x, type: e.target.value } : x))}>
                      {DOC_TYPES.map((d) => <option key={d.v} value={d.v}>{d.label}</option>)}
                    </select>
                  </td>
                  <td>
                    <input value={it.spec} disabled={busy} style={{ width: 170 }}
                      placeholder={it.type === 'plans' ? t({ en: 'T-drawings, e.g. 47-67', fil: 'T-drawings, hal. 47-67' }) : t({ en: 'Div 27/28, e.g. 220-260', fil: 'Div 27/28, hal. 220-260' })}
                      onChange={(e) => setItems((s) => s.map((x, j) => j === i ? { ...x, spec: e.target.value } : x))} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <small className="muted">
          {t({ en: 'For each file pick what it is.', fil: 'Para sa bawat file, piliin kung ano ito.' })} <strong>{t({ en: 'Tell me which pages to read', fil: 'Sabihin kung aling mga pahina ang babasahin' })}</strong> {t({ en: '— only those go to the AI (fast, accurate, lower cost).', fil: '— iyon lang ang mapupunta sa AI (mabilis, tumpak, mas mura).' })}
          {' '}{t({ en: 'For the', fil: 'Para sa' })} <strong>Plans</strong>{t({ en: ': open your PDF and find the T-drawings (legend, notes, schedules, riser); enter their page numbers like', fil: ': buksan ang iyong PDF at hanapin ang T-drawings (legend, notes, schedules, riser); ilagay ang kanilang page numbers tulad ng' })} <code>47-67</code>, {t({ en: 'or', fil: 'o' })} <code>3,5,12-15</code>.
          {' '}{t({ en: 'For the', fil: 'Para sa' })} <strong>Spec</strong>{t({ en: ': enter the Division 27 and 28 pages, e.g.', fil: ': ilagay ang mga pahina ng Division 27 at 28, hal.' })} <code>220-260</code>.
          {' '}{t({ en: 'Leave blank or type', fil: 'Iwanang blangko o i-type ang' })} <code>AUTO</code> {t({ en: 'to read the entire file (only for small PDFs, <25 pages).', fil: 'para basahin ang buong file (para lang sa maliliit na PDF, <25 pahina).' })}
        </small>
        <div style={{ marginTop: 10 }}>
          <button className="btn btn-primary" onClick={analyze} disabled={busy || !items.length}>
            {busy ? t({ en: 'Analyzing…', fil: 'Sinusuri…' }) : t({ en: 'Analyze documents', fil: 'Suriin ang mga dokumento' })}
          </button>
        </div>
        {busy && <p className="muted" style={{ marginBottom: 0 }}>{status}</p>}
        {!busy && status && <p className="muted" style={{ marginBottom: 0 }}>{status}</p>}
        {source === 'sample' && (
          <p className="muted" style={{ marginBottom: 0 }}>
            ⚠️ {t({ en: 'No Gemini key configured — used the', fil: 'Walang naka-configure na Gemini key — ginamit ang' })} <strong>{t({ en: 'bundled sample extraction', fil: 'kasamang sample extraction' })}</strong> {t({ en: 'so you can see the full flow. Add', fil: 'para makita mo ang buong flow. Idagdag ang' })} <code>GEMINI_API_KEYS</code> {t({ en: 'to read your real documents.', fil: 'para basahin ang iyong totoong mga dokumento.' })}
          </p>
        )}
        {source === 'unavailable' && (
          <div className="banner banner-danger" style={{ marginTop: 10 }}>
            <strong>{t({ en: 'The AI couldn’t read this document just now.', fil: 'Hindi mabasa ng AI ang dokumentong ito sa ngayon.' })}</strong> {aiWarning || t({ en: 'Google’s models were busy.', fil: 'Abala ang mga model ng Google.' })}
            <div style={{ marginTop: 4 }}>{t({ en: 'You can still pick disciplines below and proceed, or simply', fil: 'Maaari ka pa ring pumili ng disciplines sa ibaba at magpatuloy, o' })} <strong>{t({ en: 'click Choose Files and upload again', fil: 'i-click ang Choose Files at mag-upload muli' })}</strong> {t({ en: 'in a minute — it usually goes through on a second try. A single legend sheet reads most reliably.', fil: 'pagkalipas ng isang minuto — kadalasan ay tumatagos ito sa pangalawang subok. Ang isang legend sheet ang pinakamatibay na nababasa.' })}</div>
          </div>
        )}
        {data?.documents?.length ? (
          <p className="muted" style={{ marginBottom: 0 }}>
            {t({ en: 'Documents:', fil: 'Mga Dokumento:' })}{' '}
            {data.documents.map((d, i) => (
              <span key={d.id}>
                {i > 0 && ', '}
                {d.r2_key ? <a href={api.documentUrl(id, d.id)} target="_blank" rel="noreferrer">{d.filename}</a> : d.filename}
              </span>
            ))}
          </p>
        ) : null}
        {error && <div className="error">{error}</div>}
      </div>

      {/* Project information (auto-filled, editable — §5.3) */}
      <ProjectInfoReview projectId={id} rows={data?.extraction ?? []} onSaved={load} />

      {/* Disciplines to estimate — chip multi-select (§5.4) */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h2>2 · {t({ en: 'Which disciplines should I estimate?', fil: 'Aling mga discipline ang dapat kong i-estimate?' })} <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>— {t({ en: 'select all that apply', fil: 'piliin lahat ng naaangkop' })}</span></h2>
        {!data?.systems?.length ? (
          <p className="muted">{t({ en: "Upload a plan set above and the disciplines the AI detects will light up here — then click to choose exactly what you'll bid.", fil: 'Mag-upload ng plan set sa itaas at lilitaw dito ang mga discipline na matutukoy ng AI — pagkatapos ay i-click para piliin kung ano talaga ang ibi-bid mo.' })}</p>
        ) : (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {[...data.systems]
                .sort((a, b) => (b.auto_selected - a.auto_selected) || a.system_name.localeCompare(b.system_name))
                .map((s) => <SystemChip key={s.id} sys={s} onToggle={() => toggle(s)} />)}
            </div>
            <p className="muted" style={{ fontSize: 12, marginBottom: 0, marginTop: 10 }}>
              <span style={{ color: 'var(--teal-dark)' }}>● {t({ en: 'Teal = selected for this bid.', fil: 'Teal = napili para sa bid na ito.' })}</span> {t({ en: 'Disciplines the AI detected (•) are pre-selected — click any chip to add or remove. Only selected disciplines flow into the count sheet, BOM and estimate.', fil: 'Ang mga discipline na natukoy ng AI (•) ay paunang napili — i-click ang anumang chip para magdagdag o mag-alis. Tanging napiling mga discipline lang ang dadaloy sa count sheet, BOM at estimate.' })}
            </p>
          </>
        )}
      </div>

      {/* Next step — always available once analysis has run */}
      {data?.systems?.length ? (
        <div className="card" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            <strong>3 · {t({ en: 'Generate the Symbol Quantity Excel', fil: 'Buuin ang Symbol Quantity Excel' })}</strong>
            <div className="muted" style={{ fontSize: 13 }}>
              {data.symbols.length > 0
                ? <>{data.symbols.length} legend symbol{data.symbols.length === 1 ? '' : 's'} {t({ en: 'ready for the count sheet.', fil: 'handa na para sa count sheet.' })}</>
                : <>{t({ en: 'No legend symbols were read yet — point Step 1 at the legend sheet (e.g. the', fil: 'Wala pang nabasang legend symbols — ituro ang Step 1 sa legend sheet (hal. ang' })} <code>T</code> {t({ en: 'or symbols-legend page), or proceed and add them on the count sheet.', fil: 'o symbols-legend page), o magpatuloy at idagdag ang mga ito sa count sheet.' })}</>}
            </div>
          </div>
          <Link className="btn btn-primary" to={`/projects/${id}/count`}>{t({ en: 'Continue to Count Sheet', fil: 'Magpatuloy sa Count Sheet' })} →</Link>
        </div>
      ) : null}

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
        {/* Legend symbols */}
        <div className="card">
          <h2>Legend symbols <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>({t({ en: 'read — not counted', fil: 'binasa — hindi binilang' })})</span></h2>
          {!data?.symbols?.length && <p className="muted">{t({ en: 'None yet.', fil: 'Wala pa.' })}</p>}
          {data?.symbols?.length ? (
            <table>
              <thead><tr><th>Tag</th><th>{t(G.description)}</th><th>System</th><th>{t({ en: 'Source', fil: 'Pinagmulan' })}</th></tr></thead>
              <tbody>
                {data.symbols.map((s) => (
                  <tr key={s.id}>
                    <td><strong>{s.symbol_tag}</strong></td>
                    <td>{s.legend_description}</td>
                    <td>{s.spec_reference ?? '—'}</td>
                    <td className="muted">{safeFirst(s.sheets_present)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>

        {/* Conflicts / RFIs feed */}
        <div className="card">
          <h2>{t({ en: 'Conflicts & ambiguities', fil: 'Mga Conflict at ambiguity' })} <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>({t({ en: 'feed Phase 10 RFIs', fil: 'pakain sa Phase 10 RFIs' })})</span></h2>
          {!flags.length && <p className="muted">{t({ en: 'None flagged.', fil: 'Walang na-flag.' })}</p>}
          {flags.map((f, i) => (
            <div key={i} style={{ borderLeft: '3px solid var(--gold)', padding: '6px 10px', marginBottom: 8 }}>
              <div><strong>{f.field.replace('flag.', '').replace(/_/g, ' ')}</strong></div>
              <div>{f.value}</div>
              <div className="muted" style={{ fontSize: 12 }}>{f.source_ref}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 16 }}>
        <ExtractionList title={t({ en: 'Specification sections', fil: 'Mga Specification section' })} rows={specs} />
        <ExtractionList title={t({ en: 'Codes & standards', fil: 'Codes at standards' })} rows={(data?.codes ?? []).map((c) => ({
          field: c.level, value: `${c.code} — ${c.title}${c.certainty === 'uncertain' ? `  (${t({ en: 'uncertain', fil: 'hindi tiyak' })})` : ''}`,
          source_ref: c.source_ref, confidence: 1, status: 'auto',
        }))} />
        <ExtractionList title={t(G.notes)} rows={notes} />
        <ExtractionList title={t({ en: 'Special requirements', fil: 'Mga Special requirement' })} rows={reqs} />
        <ExtractionList title={t({ en: 'Alternates', fil: 'Mga Alternate' })} rows={scope('alternate')} />
        <ExtractionList title={t({ en: 'Allowances / Exclusions', fil: 'Mga Allowance / Exclusion' })} rows={[...scope('allowance'), ...scope('exclusion')]} />
      </div>
    </div>
  );
}

function SystemChip({ sys, onToggle }: { sys: SystemRow; onToggle: () => void }) {
  const { t } = useLang();
  const ev: string[] = (() => { try { return JSON.parse(sys.evidence ?? '[]'); } catch { return []; } })();
  const selected = !!sys.selected;
  const detected = !!sys.auto_selected;
  const tip = detected
    ? `${t({ en: 'Detected', fil: 'Natukoy' })} · ${Math.round((sys.confidence ?? 0) * 100)}% ${t({ en: 'confidence', fil: 'confidence' })}${ev[0] ? `\n${ev[0]}` : ''}`
    : t({ en: 'Not detected — click to add to this bid', fil: 'Hindi natukoy — i-click para idagdag sa bid na ito' });
  return (
    <button type="button" onClick={onToggle} title={tip}
      style={{
        border: `1.5px solid ${selected ? 'var(--teal)' : 'var(--gray-300)'}`,
        background: selected ? 'var(--teal-light)' : 'var(--white)',
        color: selected ? 'var(--teal-dark)' : 'var(--gray-700)',
        borderRadius: 999, padding: '7px 14px', fontSize: 13, fontWeight: 600,
        cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'inherit',
      }}>
      {selected && <span aria-hidden>✓</span>}
      {sys.system_name}
      {detected && <span title={t({ en: 'Detected by the AI', fil: 'Natukoy ng AI' })} style={{ color: 'var(--gold-dark)', fontSize: 14, lineHeight: 1 }}>•</span>}
    </button>
  );
}

function ExtractionList({ title, rows }: { title: string; rows: ExtractionRow[] }) {
  if (!rows.length) return null;
  return (
    <div className="card">
      <h2>{title}</h2>
      <table>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>{r.value}</td>
              <td className="muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{r.source_ref}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function safeFirst(json: string | null): string {
  try { const a = JSON.parse(json ?? '[]'); return Array.isArray(a) ? a[0] ?? '' : ''; } catch { return ''; }
}

// Auto-filled project info (§5.3): every field shows its source + confidence and
// is editable. Low-confidence or missing values are flagged "needs review".
const INFO_FIELDS: { key: string; label: Bilingual }[] = [
  { key: 'metadata.project_name', label: { en: 'Project name', fil: 'Pangalan ng proyekto' } },
  { key: 'metadata.customer_name', label: { en: 'Customer / Owner', fil: 'Customer / Owner' } },
  { key: 'metadata.gc_name', label: { en: 'General Contractor', fil: 'General Contractor' } },
  { key: 'metadata.gc_contact', label: { en: 'GC contact', fil: 'GC contact' } },
  { key: 'metadata.architect', label: { en: 'Architect', fil: 'Architect' } },
  { key: 'metadata.engineer', label: { en: 'Engineer', fil: 'Engineer' } },
  { key: 'metadata.job_address', label: { en: 'Project address', fil: 'Address ng proyekto' } },
  { key: 'metadata.county', label: { en: 'County', fil: 'County' } },
  { key: 'metadata.state', label: { en: 'State', fil: 'State' } },
  { key: 'metadata.bid_date', label: { en: 'Bid date', fil: 'Petsa ng bid' } },
  { key: 'metadata.bid_due_date', label: { en: 'Bid due date', fil: 'Takdang petsa ng bid' } },
  { key: 'metadata.pw_jurisdiction', label: { en: 'Prevailing-wage jurisdiction', fil: 'Prevailing-wage jurisdiction' } },
  { key: 'metadata.davis_bacon', label: { en: 'Davis-Bacon applies (yes/no)', fil: 'May Davis-Bacon (oo/hindi)' } },
];

function ProjectInfoReview({ projectId, rows, onSaved }: { projectId: string; rows: ExtractionRow[]; onSaved: () => void }) {
  const { t } = useLang();
  const has = rows.some((r) => r.field.startsWith('metadata.'));
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h2>{t({ en: 'Project information', fil: 'Impormasyon ng proyekto' })} <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>— {t({ en: 'auto-filled from the documents; edit or confirm each field', fil: 'awtomatikong napuno mula sa mga dokumento; i-edit o kumpirmahin ang bawat field' })}</span></h2>
      {!has && <p className="muted">{t({ en: 'Upload a document to auto-fill project information.', fil: 'Mag-upload ng dokumento para awtomatikong mapuno ang impormasyon ng proyekto.' })}</p>}
      {has && (
        <table>
          <thead><tr><th>{t({ en: 'Field', fil: 'Field' })}</th><th>{t({ en: 'Value', fil: 'Halaga' })}</th><th>{t({ en: 'Source', fil: 'Pinagmulan' })}</th><th>{t({ en: 'Confidence', fil: 'Confidence' })}</th><th></th></tr></thead>
          <tbody>
            {INFO_FIELDS.map((f) => (
              <EditableField key={f.key} projectId={projectId} field={f.key} label={f.label}
                row={rows.find((r) => r.field === f.key)} onSaved={onSaved} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function EditableField({ projectId, field, label, row, onSaved }: {
  projectId: string; field: string; label: Bilingual; row: ExtractionRow | undefined; onSaved: () => void;
}) {
  const { t } = useLang();
  const [val, setVal] = useState(row?.value ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { setVal(row?.value ?? ''); setSaved(false); }, [row?.value]);

  const conf = row?.confidence ?? 0;
  const needsReview = !row || !row.value || (row.status === 'auto' && conf < 0.6);
  const dirty = (row?.value ?? '') !== val;

  async function save() {
    setSaving(true);
    try { await api.editField(projectId, { field, value: val }); setSaved(true); onSaved(); }
    catch { /* surfaced on reload */ }
    finally { setSaving(false); }
  }

  return (
    <tr style={needsReview ? { background: 'rgba(197,16,16,0.05)' } : undefined}>
      <td style={{ whiteSpace: 'nowrap' }}>
        <strong>{t(label)}</strong>
        {needsReview && <span className="pill pill-needs_review" style={{ marginLeft: 8 }}>{t({ en: 'needs review', fil: 'kailangang suriin' })}</span>}
      </td>
      <td>
        <input value={val} onChange={(e) => { setVal(e.target.value); setSaved(false); }} placeholder="—"
          style={{ width: '100%', padding: '6px 9px', border: '1px solid var(--gray-300)', borderRadius: 6, font: 'inherit' }} />
      </td>
      <td className="muted" style={{ fontSize: 12 }}>{row?.source_ref ?? '—'}</td>
      <td className="muted" style={{ fontSize: 12 }}>{row ? (row.status !== 'auto' ? row.status : `${Math.round(conf * 100)}%`) : '—'}</td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {dirty ? <button className="btn btn-ghost btn-sm" onClick={save} disabled={saving}>{saving ? t(G.saving) : t(G.save)}</button>
          : saved ? <span className="muted" style={{ fontSize: 12 }}>{t(G.saved)} ✓</span> : null}
      </td>
    </tr>
  );
}
